<template>
  <div class="space-y-8">
    <!-- Search Section -->
    <div class="space-y-4">
      <div class="relative group">
        <Search
          class="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search skills"
          class="w-full pl-14 pr-12 py-4 rounded-full border border-border/50 bg-white dark:bg-muted/10 text-foreground outline-none focus:ring-1 focus:ring-primary/20 focus:border-primary/30 transition-all text-[16px]"
          @keyup.enter="handleEnter"
        />
        <ChevronDown
          class="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
        />
        <!-- Dropdown for search results -->
        <div
          v-if="searchQuery && filteredSkills.length > 0"
          class="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto z-10 flex flex-col py-2"
        >
          <button
            v-for="skill in filteredSkills"
            :key="skill.skill_id"
            @click="toggleSkill(skill)"
            class="w-full text-left px-4 py-3 hover:bg-muted text-sm text-foreground transition-colors"
          >
            {{ skill.skill_name }}
            <span v-if="isSelected(skill)" class="float-right text-primary"
              >✓</span
            >
          </button>
        </div>
      </div>
    </div>

    <!-- Suggested Skills -->
    <div class="space-y-4">
      <p class="text-[14px] font-semibold text-foreground">
        Suggested skills for <span class="text-primary">{{ userRole }}</span>
      </p>
      <div v-if="loading" class="text-sm text-muted-foreground animate-pulse">
        Loading skills...
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <button
          v-for="skill in suggestedSkills"
          :key="skill.skill_id"
          @click="toggleSkill(skill)"
          class="px-5 py-2.5 rounded-full border text-sm font-medium transition-all"
          :class="[
            isSelected(skill)
              ? 'border-primary text-primary bg-primary/10 ring-1 ring-primary/20'
              : 'border-border/40 bg-muted/20 text-muted-foreground hover:border-primary/50 hover:text-primary',
          ]"
        >
          {{ skill.skill_name }}
        </button>
      </div>
    </div>

    <!-- Selected Skills List -->
    <div
      v-if="selectedSkills.length > 0"
      class="space-y-4 pt-4 border-t border-border/40"
    >
      <p class="text-[15px] font-bold text-foreground">Skills selected</p>
      <div class="space-y-3">
        <div
          v-for="(skill, index) in selectedSkills"
          :key="index"
          class="flex items-center justify-between p-5 rounded-2xl border border-border/40 bg-card/50 hover:bg-muted/10 transition-all group"
        >
          <span class="text-[16px] font-medium text-foreground">{{
            skill.skill_name || skill
          }}</span>
          <button
            @click="removeSkill(index)"
            class="p-2 text-muted-foreground/40 hover:text-destructive transition-colors"
          >
            <Trash2 class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Search, ChevronDown, Trash2 } from "lucide-vue-next";
import apiClient from "@/axios";

export default {
  name: "SkillsManager",
  components: {
    Search,
    ChevronDown,
    Trash2,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    userRole: {
      type: String,
      default: "Frontend Engineers",
    },
  },
  emits: ["update:value"],
  data() {
    return {
      searchQuery: "",
      suggestedSkills: [],
      availableSkills: [],
      selectedSkills: [...this.value],
      loading: true,
    };
  },
  computed: {
    filteredSkills() {
      if (!this.searchQuery) return [];
      const query = this.searchQuery.toLowerCase();
      return this.availableSkills.filter(
        (s) => s.skill_name && s.skill_name.toLowerCase().includes(query),
      );
    },
  },
  mounted() {
    this.fetchSkills();
  },
  methods: {
    async fetchSkills() {
      try {
        const response = await apiClient.get("/web/skills");
        this.availableSkills = response.data.data;
        // Default to first 10 skills as suggested if available
        this.suggestedSkills = this.availableSkills.slice(0, 10);
      } catch (error) {
        console.error("Error fetching skills:", error);
      } finally {
        this.loading = false;
      }
    },
    isSelected(skill) {
      if (!skill) return false;
      const skillName = (skill.skill_name || skill).toLowerCase();
      return this.selectedSkills.some(
        (s) => (s.skill_name || s).toLowerCase() === skillName,
      );
    },
    handleEnter() {
      if (this.filteredSkills.length > 0) {
        this.toggleSkill(this.filteredSkills[0]);
      }
    },
    toggleSkill(skill) {
      if (!skill) return;

      let isString = typeof skill === "string";
      const skillName = isString ? skill.trim() : skill.skill_name;

      const index = this.selectedSkills.findIndex(
        (s) => (s.skill_name || s).toLowerCase() === skillName.toLowerCase(),
      );

      if (index === -1) {
        // Find full skill object if it's a string
        let skillToAdd = skill;
        if (isString) {
          const found = this.availableSkills.find(
            (s) => s.skill_name.toLowerCase() === skillName.toLowerCase(),
          );
          if (found) skillToAdd = found;
        }
        this.selectedSkills.push(skillToAdd);
      } else {
        this.selectedSkills.splice(index, 1);
      }
      this.$emit("update:value", this.selectedSkills);
      this.searchQuery = "";
    },
    removeSkill(index) {
      this.selectedSkills.splice(index, 1);
      this.$emit("update:value", this.selectedSkills);
    },
  },
};
</script>
